import { getCachedUserData } from '@util/thrive-user-data'
import { DeploymentEnv } from '@enums/deployment-env'
import * as Sentry from '@sentry/browser'

import { DEPLOYMENT_ENV, SENTRY_IO_DSN, USE_SENTRY_IO } from '../../config'
import { IGNORE_ERRORS } from './ignore-errors'

/**
 * intialize use of Sentry.io
 *
 * default usage should be "off" as we get charged on a volume license
 * amount. Use it locally to test it works as expected, and staging and
 * production environments
 */
export function onPageLoad() {
  // USE_SENTRY_IO comes in as a string because it's from the env file
  if (!JSON.parse(USE_SENTRY_IO) || !SENTRY_IO_DSN) return
  // ignore for Cypress integration tests
  if (window.Cypress) return

  // https://docs.sentry.io/error-reporting/configuration/?platform=javascript#send-default-pii%20-%%7D
  Sentry.init({
    dsn: SENTRY_IO_DSN,
    debug: DEPLOYMENT_ENV === DeploymentEnv.local,
    // safely casting; page load functions cannot be called without DEPLOYMENT_ENV set
    environment: DEPLOYMENT_ENV as string,

    // https://docs.sentry.io/enriching-error-data/breadcrumbs/?platform=javascript
    beforeBreadcrumb(breadcrumb) {
      /**
       * most logging is useful for local development only — it can also pick up
       * PII because Amplify data gets logged out locally, so ignore it!
       */
      return breadcrumb.category === 'console' ? null : breadcrumb
    },
    ignoreErrors: IGNORE_ERRORS,
  })
}

/**
 * wrapper for Sentry's captureException — include the Thrive User Id and
 * company Id if possible
 */
export function captureException(err: Error) {
  // include the user's Thrive User ID and Company ID if available
  const { thriveUserId, companyId } = getCachedUserData()

  Sentry.configureScope((scope) => {
    if (thriveUserId) scope.setUser({ id: thriveUserId })
    if (companyId) scope.setExtra('company_id', companyId)
  })

  Sentry.captureException(err)
}
